import type {ThreadUserMessagePart} from '@assistant-ui/core';
import {useAui} from '@assistant-ui/react';
import {useLayoutEffect, useRef} from 'react';

import {
  draftKey,
  type SelectedRun,
  useDraftStore,
} from '../../agentChat/composer';
import type {
  SendMessageOptions,
  WBAgentController,
} from '../../agentChat/controller';
import {wbAgentControllerStore} from '../../agentChat/controller';
import {WANDB_DATA_PART_NAME} from '../../agentChat/messageConversions';
import {useActiveThread} from '../../agentChat/store';
import type {ContentPart} from '../../agentChat/store/thread';
import {isAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

type Aui = ReturnType<typeof useAui>;
type TrackSent = ReturnType<typeof useTrackWBAgentComposer>['trackMessageSent'];

/**
 * WBAgentControllerBridge registers the imperative controller handle while the
 * agent chat is mounted, so buttons anywhere in the app can open the chat or
 * send a message. It must render inside `AuiProvider` (it reads `useAui`) and
 * is mounted only when the feature is available; the handle is cleared on
 * unmount. Renders nothing.
 */
export const WBAgentControllerBridge = (): null => {
  const aui = useAui();
  const {trackMessageSent} = useTrackWBAgentComposer();
  const activeThread = useActiveThread();

  // NOTE: Refs keep the tracker and thread id out of the effect deps, so the
  // handle re-registers only on `aui` changes.
  const trackRef = useRef(trackMessageSent);
  trackRef.current = trackMessageSent;
  const threadIdRef = useRef<string | null>(activeThread?.id ?? null);
  threadIdRef.current = activeThread?.id ?? null;

  // NOTE: Layout effect clears the handle before paint, beating any click that
  // would otherwise reach a handle whose runtime is being torn down.
  useLayoutEffect(
    () =>
      wbAgentControllerStore.registerHandle(
        createController(
          aui,
          () => trackRef.current,
          () => threadIdRef.current
        )
      ),
    [aui]
  );

  return null;
};

function createController(
  aui: Aui,
  getTrackSent: () => TrackSent,
  getThreadId: () => string | null
): WBAgentController {
  const openChat = () => {
    useWBAgentWindowStore.getState().setOpen(true);
  };

  return {
    addRunToDraft(run: SelectedRun) {
      if (isAgentChatReadonly()) {
        return {ok: false, reason: 'readonly'};
      }

      useDraftStore.getState().addRun(draftKey(getThreadId()), run);
      return {ok: true};
    },
    openChat,
    openChatToThread(threadId: string) {
      aui.threads().switchToThread(threadId);
      openChat();
    },
    sendMessage(
      prompt: string | ContentPart[],
      attachments?: ContentPart[],
      opts?: SendMessageOptions
    ) {
      const {newChat = true} = opts ?? {};
      const content = buildUserContent(messageParts(prompt, attachments));

      // Nothing to send is a no-op; don't open or switch threads.
      if (content.length === 0) {
        return {ok: false, reason: 'empty'};
      }

      if (newChat) {
        aui.threads().switchToNewThread();
      }

      const readonly = isAgentChatReadonly();
      if (!readonly) {
        aui.thread().append({role: 'user', content});
        getTrackSent()('programmatic');
      }

      openChat();
      return readonly ? {ok: false, reason: 'readonly'} : {ok: true};
    },
  };
}

// Normalize the two call shapes into the ordered parts of one user message. A
// parts array is already the full composite message (text + structured context
// in the caller's order). A string keeps the legacy shape: leading attachments,
// then the trimmed text.
function messageParts(
  prompt: string | ContentPart[],
  attachments?: ContentPart[]
): ContentPart[] {
  if (typeof prompt !== 'string') {
    return prompt;
  }
  const parts: ContentPart[] = [...(attachments ?? [])];
  const text = prompt.trim();
  if (text.length > 0) {
    parts.push({type: 'text', text, status: 'complete'});
  }
  return parts;
}

// Render the user-authored parts as AUI message parts: text stays text,
// structured wandb parts (run/conversation/scope/page context) become `data`
// parts, images become images. Parts with no user-message representation
// (thinking, tool calls, unknown) are dropped.
function buildUserContent(parts: ContentPart[]): ThreadUserMessagePart[] {
  const content: ThreadUserMessagePart[] = [];
  for (const part of parts) {
    switch (part.type) {
      case 'text':
        if (part.text.length > 0) {
          content.push({type: 'text', text: part.text});
        }
        break;
      case 'image':
        content.push({
          type: 'image',
          image: `data:${part.mediaType};base64,${part.base64Data}`,
        });
        break;
      case 'wandb-entity':
      case 'wandb-project':
      case 'wandb-run':
      case 'wandb-call':
      case 'wandb-conversation':
      case 'wandb-artifact-collection':
      case 'wandb-workspace-panel':
      case 'wandb-web-page-state':
        content.push({type: 'data', name: WANDB_DATA_PART_NAME, data: part});
        break;
      default:
        break;
    }
  }
  return content;
}
